import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelSuppliesPOS({ onBack, onOpenReplenish }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [supplies, setSupplies] = useState([]);
  const [stays, setStays] = useState([]);

  const [selectedStayId, setSelectedStayId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("room_charge");
  const [notes, setNotes] = useState("");

  const [selectedSupplyId, setSelectedSupplyId] = useState("");
  const [selectedQty, setSelectedQty] = useState(1);
  const [cart, setCart] = useState([]);

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4500);
  };

  const activeStays = useMemo(() => {
    return stays.filter((s) =>
      ["checked_in", "active"].includes(String(s.status || "").toLowerCase())
    );
  }, [stays]);

  const selectedSupply = useMemo(() => {
    return supplies.find((s) => Number(s.id) === Number(selectedSupplyId)) || null;
  }, [supplies, selectedSupplyId]);

  const selectedStay = useMemo(() => {
    return activeStays.find((s) => Number(s.id) === Number(selectedStayId)) || null;
  }, [activeStays, selectedStayId]);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, row) => sum + Number(row.line_total || 0), 0);
  }, [cart]);

  const loadSupplies = async () => {
    const res = await hotelApi.get("/supplies");
    setSupplies(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const loadStays = async () => {
    const res = await hotelApi.get("/stays");
    setStays(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadSupplies(), loadStays()]);
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to refresh data.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const resetSale = () => {
    setSelectedStayId("");
    setPaymentMethod("room_charge");
    setNotes("");
    setSelectedSupplyId("");
    setSelectedQty(1);
    setCart([]);
  };

  const addToCart = () => {
    if (!selectedSupply) {
      showMsg("⚠️ Select a supply item.");
      return;
    }

    const qty = Math.max(1, Number(selectedQty || 1));
    const stock = Number(selectedSupply.qty_on_hand || 0);

    if (qty > stock) {
      showMsg("⚠️ Quantity exceeds stock on hand.");
      return;
    }

    setCart((prev) => {
      const existing = prev.find(
        (x) => Number(x.supply_id) === Number(selectedSupply.id)
      );

      if (existing) {
        const newQty = Number(existing.qty) + qty;
        if (newQty > stock) {
          showMsg("⚠️ Combined quantity exceeds stock on hand.");
          return prev;
        }

        return prev.map((x) =>
          Number(x.supply_id) === Number(selectedSupply.id)
            ? {
                ...x,
                qty: newQty,
                line_total: newQty * Number(x.unit_price || 0),
              }
            : x
        );
      }

      return [
        ...prev,
        {
          supply_id: Number(selectedSupply.id),
          name: selectedSupply.item_name,
          qty,
          unit_price: Number(selectedSupply.unit_price || 0),
          line_total: qty * Number(selectedSupply.unit_price || 0),
        },
      ];
    });

    setSelectedSupplyId("");
    setSelectedQty(1);
  };

  const updateCartQty = (index, value) => {
    const qty = Math.max(1, Number(value || 1));

    setCart((prev) => {
      const row = prev[index];
      if (!row) return prev;

      const stockRow =
        supplies.find((s) => Number(s.id) === Number(row.supply_id)) || null;
      const stock = Number(stockRow?.qty_on_hand || 0);

      if (qty > stock) {
        showMsg("⚠️ Quantity exceeds stock on hand.");
        return prev;
      }

      return prev.map((r, i) =>
        i === index
          ? {
              ...r,
              qty,
              line_total: qty * Number(r.unit_price || 0),
            }
          : r
      );
    });
  };

  const removeCartLine = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const printReceipt = (payload) => {
    const w = window.open("", "_blank", "width=430,height=760");
    if (!w) {
      showMsg("⚠️ Popup blocked. Allow popups to print.");
      return;
    }

    const esc = (v) =>
      String(v ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

    w.document.write(`
      <html>
        <head>
          <title>Supplies Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
            h2 { margin: 0 0 12px; }
            .line { display:flex; justify-content:space-between; margin:6px 0; }
            table { width:100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border-bottom:1px solid #ddd; padding:6px 4px; text-align:left; font-size:12px; }
            .total { font-weight:700; margin-top:10px; padding-top:10px; border-top:1px dashed #999; }
          </style>
        </head>
        <body>
          <h2>Guest Supplies Receipt</h2>
          <div class="line"><span>Stay</span><span>${esc(payload.stay_label || "Walk-in")}</span></div>
          <div class="line"><span>Payment</span><span>${esc(payload.payment_method || "-")}</span></div>
          <div class="line"><span>Date</span><span>${esc(payload.date || "-")}</span></div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${(payload.items || [])
                .map(
                  (row) => `
                    <tr>
                      <td>${esc(row.name)}</td>
                      <td>${Number(row.qty || 0).toFixed(2)}</td>
                      <td>${Number(row.unit_price || 0).toFixed(2)}</td>
                      <td>${Number(row.line_total || 0).toFixed(2)}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>

          <div class="line total"><span>Total</span><span>${Number(payload.total || 0).toFixed(2)}</span></div>
        </body>
      </html>
    `);

    w.document.close();
    w.focus();
    w.print();
  };

  const handleCharge = async () => {
    try {
      if (!selectedStayId) {
        showMsg("⚠️ Select an active stay.");
        return;
      }

      if (!selectedStay) {
        showMsg("⚠️ Invalid stay selected.");
        return;
      }

      if (cart.length === 0) {
        showMsg("⚠️ Cart is empty.");
        return;
      }

      setLoading(true);

      const payload = {
        stay_id: Number(selectedStayId),
        payment_method: paymentMethod || "room_charge",
        notes: notes.trim() || null,
        items: cart.map((x) => ({
          supply_id: Number(x.supply_id),
          qty: Number(x.qty),
        })),
      };

      const res = await hotelApi.post("/supplies/sell", payload);

      if (res.data?.success === false) {
        showMsg("❌ " + (res.data?.error || res.data?.message || "Sale failed"));
        return;
      }

      printReceipt({
        stay_label: `${selectedStay.guest_name || "Guest"} / Room ${selectedStay.room_no || "-"}`,
        payment_method: paymentMethod,
        date: new Date().toLocaleString(),
        items: cart,
        total: cartTotal,
      });

      showMsg("✅ Guest supply sale saved.");
      resetSale();
      await refreshAll();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Sale failed.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <h2 style={{ margin: 0 }}>🛍️ Hotel Supplies POS</h2>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={styles.btn} onClick={refreshAll} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={() => onOpenReplenish?.()}
            disabled={loading}
          >
            Replenish Supplies
          </button>
          <button style={styles.btn2} onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Create Supply Sale</div>

          <div style={styles.form}>
            <div>
              <label style={styles.label}>Active Stay</label>
              <select
                style={styles.input}
                value={selectedStayId}
                onChange={(e) => setSelectedStayId(e.target.value)}
                disabled={loading}
              >
                <option value="">Select active stay...</option>
                {activeStays.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.guest_name || "Guest"} - Room {s.room_no || "-"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.label}>Payment Method</label>
              <select
                style={styles.input}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={loading}
              >
                <option value="room_charge">Room Charge</option>
                <option value="cash">Cash</option>
                <option value="mpesa">Mpesa</option>
                <option value="bank">Bank</option>
              </select>
            </div>

            <div>
              <label style={styles.label}>Notes</label>
              <textarea
                rows={3}
                style={styles.textarea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
              />
            </div>

            <div style={styles.selectionCard}>
              <div style={styles.selectionGrid}>
                <div>
                  <label style={styles.label}>Supply Item</label>
                  <select
                    style={styles.input}
                    value={selectedSupplyId}
                    onChange={(e) => setSelectedSupplyId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select supply...</option>
                    {supplies.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.item_name} - {Number(s.unit_price || 0).toFixed(2)} - Stock {Number(s.qty_on_hand || 0).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={styles.label}>Qty</label>
                  <input
                    type="number"
                    min="1"
                    style={styles.input}
                    value={selectedQty}
                    onChange={(e) => setSelectedQty(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div style={{ alignSelf: "end" }}>
                  <button
                    type="button"
                    style={styles.primary}
                    onClick={addToCart}
                    disabled={loading}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>

            <div style={styles.cardTitle}>Cart</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item</th>
                    <th style={styles.th}>Qty</th>
                    <th style={styles.th}>Unit Price</th>
                    <th style={styles.th}>Line Total</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={5}>Cart is empty.</td>
                    </tr>
                  ) : (
                    cart.map((row, idx) => (
                      <tr key={`${row.supply_id}-${idx}`}>
                        <td style={styles.td}>{row.name}</td>
                        <td style={styles.td}>
                          <input
                            style={{ ...styles.input, minWidth: 90 }}
                            type="number"
                            min="1"
                            value={row.qty}
                            onChange={(e) => updateCartQty(idx, e.target.value)}
                          />
                        </td>
                        <td style={styles.td}>{Number(row.unit_price || 0).toFixed(2)}</td>
                        <td style={styles.td}>{Number(row.line_total || 0).toFixed(2)}</td>
                        <td style={styles.td}>
                          <button
                            type="button"
                            style={styles.removeBtn}
                            onClick={() => removeCartLine(idx)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={styles.td} colSpan={3}>
                      <b>Total</b>
                    </td>
                    <td style={styles.td}>
                      <b>{cartTotal.toFixed(2)}</b>
                    </td>
                    <td style={styles.td}>—</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={resetSale}
                disabled={loading}
              >
                Clear
              </button>
              <button
                type="button"
                style={styles.primary}
                onClick={handleCharge}
                disabled={loading || !selectedStayId || cart.length === 0}
              >
                {loading ? "Saving..." : "Charge Supplies"}
              </button>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Available Supplies</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Price</th>
                  <th style={styles.th}>Stock</th>
                </tr>
              </thead>
              <tbody>
                {supplies.length === 0 ? (
                  <tr>
                    <td style={styles.td} colSpan={4}>No supplies found.</td>
                  </tr>
                ) : (
                  supplies.map((s) => (
                    <tr key={s.id}>
                      <td style={styles.td}>{s.item_name}</td>
                      <td style={styles.td}>{s.category || "—"}</td>
                      <td style={styles.td}>{Number(s.unit_price || 0).toFixed(2)}</td>
                      <td style={styles.td}>{Number(s.qty_on_hand || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={styles.cardTitle}>Active Stays</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Stay ID</th>
                    <th style={styles.th}>Guest</th>
                    <th style={styles.th}>Room</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeStays.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={4}>No active stays found.</td>
                    </tr>
                  ) : (
                    activeStays.map((s) => (
                      <tr key={s.id}>
                        <td style={styles.td}>{s.id}</td>
                        <td style={styles.td}>{s.guest_name || "—"}</td>
                        <td style={styles.td}>{s.room_no || "—"}</td>
                        <td style={styles.td}>{s.status || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    padding: 12,
    background: "#f8fafc",
    minHeight: "100vh",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1.15fr .85fr",
    gap: 12,
    alignItems: "start",
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 12,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)",
  },
  cardTitle: {
    fontWeight: 900,
    marginBottom: 10,
    color: "#0f172a",
    fontSize: 18,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 800,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
    resize: "vertical",
  },
  selectionCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
  },
  selectionGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr auto",
    gap: 10,
    alignItems: "end",
  },
  primary: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  removeBtn: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "none",
    background: "#dc2626",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn2: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#f1f5f9",
    fontWeight: 800,
    cursor: "pointer",
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 12,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#475569",
    borderBottom: "1px solid rgba(15,23,42,.12)",
    padding: "9px 8px",
    whiteSpace: "nowrap",
    background: "#f8fafc",
  },
  td: {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(15,23,42,.08)",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
};